import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";

import type { UseFiltersResult } from "#app/lib/filters/types";
import { useFilters } from "#app/lib/hooks/use-filters";
import { issueFilterConfig } from "#app/lib/filters/configs/issue-filter-config";
import { useAnalytics, AnalyticsContext, IssueEvent } from "#app/lib/analytics";

type IssueFilterContextValue = {
  filters: UseFiltersResult;
  filterPanelOpen: boolean;
  openFilterPanel: () => void;
  closeFilterPanel: () => void;
  toggleFilterPanel: () => void;
};

const IssueFilterContext = createContext<IssueFilterContextValue | null>(null);

/**
 * Shared issue filter state for an issue screen (workflow, team, or member).
 * Both the board and the list view consume this context to apply panel filters.
 * Scope-agnostic — the scope flows by prop, not through here.
 */
export function IssueFilterProvider({ children }: { children: React.ReactNode }) {
  const filters = useFilters(issueFilterConfig);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const { track } = useAnalytics();

  const openFilterPanel = useCallback(() => setFilterPanelOpen(true), []);
  const closeFilterPanel = useCallback(() => setFilterPanelOpen(false), []);
  const toggleFilterPanel = useCallback(() => setFilterPanelOpen((v) => !v), []);

  // One `apply_filter` event per committed change to the active filter set, for
  // every issue screen (board/list/member) that mounts this provider. We track
  // WHICH categories are active (not just the count) — the dimensions people
  // filter on are the signal a future orchestration layer needs. Keyed on a
  // (count + category-set) signature so panel open/close alone never fires;
  // mount and clear-to-empty are skipped (clearing isn't "applying").
  const activeState =
    filters.activeMode === "simple" ? filters.filterState : filters.advancedState;
  const categories = useMemo(
    () =>
      Array.from(
        new Set(
          activeState.blocks
            .flatMap((b) => b.filters.map((f) => f.field))
            .filter(Boolean),
        ),
      ).sort(),
    [activeState],
  );
  const signature = `${filters.activeFilterCount}|${categories.join(",")}`;
  const prevSignature = useRef<string | null>(null);
  useEffect(() => {
    if (prevSignature.current === null) {
      prevSignature.current = signature;
      return;
    }
    if (prevSignature.current === signature) return;
    prevSignature.current = signature;
    if (filters.activeFilterCount === 0) return;
    track({
      context: AnalyticsContext.Issue,
      event: IssueEvent.ApplyFilter,
      data: { count: filters.activeFilterCount, categories },
    });
  }, [signature, filters.activeFilterCount, categories, track]);

  const value = React.useMemo<IssueFilterContextValue>(
    () => ({
      filters,
      filterPanelOpen,
      openFilterPanel,
      closeFilterPanel,
      toggleFilterPanel,
    }),
    [filters, filterPanelOpen, openFilterPanel, closeFilterPanel, toggleFilterPanel],
  );

  return (
    <IssueFilterContext.Provider value={value}>
      {children}
    </IssueFilterContext.Provider>
  );
}

export function useIssueFilters(): IssueFilterContextValue {
  const ctx = useContext(IssueFilterContext);
  if (!ctx) {
    throw new Error("useIssueFilters must be used within an IssueFilterProvider");
  }
  return ctx;
}

/**
 * Provider-optional read for screens that render both inside and outside an
 * issue screen's filter scope (the `Dashboard` host: the workflow/team tabs
 * mount a provider, `/my-work` doesn't). Null means no panel on this surface —
 * consumers hide their filter affordances rather than throwing.
 */
export function useIssueFiltersOptional(): IssueFilterContextValue | null {
  return useContext(IssueFilterContext);
}
